import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionRow, AREA, BigButton, Card, FIELD, FieldLabel, ICONS, LineIcon, LinkButton, SectionLabel, Segmented, StatusPill } from '../../components/ui/dg';
import { useAuth } from '../../contexts/AuthContext';
import { CampaignAudience, CampaignLang, CampaignText, createCampaign } from '../../api/services/marketingService';
import {
  CampaignDraft,
  getCampaignDraft,
  getEmailTemplate,
  getEmailTemplates,
  previewCampaignContent,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
  sendEmailTemplateTest,
  TemplateDetail,
  TemplateFields,
  TemplateGroup,
  TemplateSource,
  TemplateSummary
} from '../../api/services/emailTemplatesService';

/**
 * E-mails : tout ce que Dingo envoie par e-mail, et les campagnes e-mail.
 *
 * Chaque modèle se lit tel qu'il part (aperçu avec des données d'exemple),
 * dans les trois langues, et se réécrit langue par langue : objet, corps,
 * texte brut. La version du code reste la référence — « Revenir au texte
 * d'origine » efface la réécriture. Là où le code compose lui-même (dates,
 * branche propriétaire/locataire, listes), la réécriture part de
 * {{default.html}}, qui garde ce passage vivant.
 *
 * Une campagne e-mail part d'un modèle (dupliqué) ou d'une page blanche ; elle
 * est mise en attente et se valide dans l'onglet Campagnes, comme les autres : rien ne
 * part d'ici vers les utilisateurs.
 */

const LANGS: { code: CampaignLang; label: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'en', label: 'English', dir: 'ltr' }
];

const AUDIENCE_LABEL: Record<string, string> = {
  all: 'Tous les comptes',
  renters: 'Comptes sans annonce',
  owners: 'Propriétaires (au moins une annonce)'
};

const SOURCE_LABEL: Record<TemplateSource, string> = {
  account: 'E-mail de compte',
  notification: 'Notification (push + e-mail)',
  campaign: 'Modèle de campagne'
};

const CAMPAIGN_VARIABLES = ['name', 'first_name', 'last_name'];
const VARIABLE_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const asideTitle = 'text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const emptyFields = (): TemplateFields => ({ subject: '', title: '', html: '', text: '' });

const stripHtml = (html: string) => html
  .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n').replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
  .replace(/[ \t]+/g, ' ').replace(/\n\s*\n\s*\n+/g, '\n\n').trim();

type Notice = { tone: 'ok' | 'warn' | 'error'; text: string; action?: { label: string; onClick: () => void } } | null;

/** Aperçu différé : on attend que la frappe se calme avant de redemander le rendu. */
function useDebouncedPreview(load: () => Promise<string>, deps: React.DependencyList, delay = 700) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const next = await loadRef.current();
        if (!cancelled) { setHtml(next); setError(null); }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Aperçu indisponible.');
      }
    }, delay);
    return () => { cancelled = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { html, error };
}

const PreviewFrame = ({ html, error, height = 'h-[560px]' }: { html: string | null; error: string | null; height?: string }) => (
  <div className="overflow-hidden">
    {error ? (
      <div className="px-3 py-8 text-center text-sm text-red-700 bg-red-50">{error}</div>
    ) : html ? (
      <iframe title="Aperçu de l’e-mail" srcDoc={html} sandbox="" className={`${height} w-full bg-white`} />
    ) : (
      <div className={`${height} animate-pulse bg-gray-50`} />
    )}
  </div>
);

const LangTabs = ({ lang, onChange, marked = [] }: { lang: CampaignLang; onChange: (lang: CampaignLang) => void; marked?: CampaignLang[] }) => (
  <div className="inline-flex rounded-xl border border-gray-200 p-0.5 text-[13px] font-medium">
    {LANGS.map((l) => (
      <button
        key={l.code}
        type="button"
        onClick={() => onChange(l.code)}
        className={`rounded-lg px-3.5 py-1.5 transition-colors ${lang === l.code ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
      >
        {l.label}
        {marked.includes(l.code) && <span className={`ml-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle ${lang === l.code ? 'bg-amber-300' : 'bg-amber-500'}`} title="Réécrit" />}
      </button>
    ))}
  </div>
);

/** Insère `{{nom}}` au curseur du dernier champ touché. */
function useInsertAtCursor(apply: (field: keyof TemplateFields, next: string) => void) {
  const last = useRef<{ field: keyof TemplateFields; el: HTMLInputElement | HTMLTextAreaElement } | null>(null);
  const track = (field: keyof TemplateFields) => (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    last.current = { field, el: event.currentTarget };
  };
  const insert = (variable: string) => {
    const target = last.current;
    if (!target) return;
    const { el, field } = target;
    const token = `{{${variable}}}`;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    apply(field, el.value.slice(0, start) + token + el.value.slice(end));
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };
  return { track, insert };
}

// --- Page -------------------------------------------------------------------------------

const ManagementEmailTemplates = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<TemplateGroup[]>([]);
  const [items, setItems] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<{ source: TemplateSource; key: string } | null>(null);
  const [composer, setComposer] = useState<{ seed: CampaignDraft | null } | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getEmailTemplates(token);
      setGroups(data.groups);
      setItems(data.items);
      setSelected((current) => current || (data.items[0] ? { source: data.items[0].source, key: data.items[0].key } : null));
    } catch (err: any) {
      setLoadError(err.message || 'Modèles indisponibles.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = q ? items.filter((i) => `${i.label} ${i.key}`.toLowerCase().includes(q)) : items;
    return groups
      .map((g) => ({ ...g, items: matches.filter((i) => i.group === g.id) }))
      .filter((g) => g.items.length > 0);
  }, [groups, items, search]);


  const duplicate = async (source: TemplateSource, key: string) => {
    if (!token) return;
    setDuplicating(true);
    setNotice(null);
    try {
      const seed = await getCampaignDraft(token, source, key);
      setComposer({ seed });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err.message || 'Duplication impossible.' });
    } finally {
      setDuplicating(false);
    }
  };

  // Après un enregistrement : le catalogue dit quelles langues sont réécrites.
  const refreshSummary = (detail: TemplateDetail) => {
    const langs = (Object.keys(detail.langs) as CampaignLang[]).filter((l) => detail.langs[l].override);
    setItems((rows) => rows.map((row) => (row.source === detail.source && row.key === detail.key
      ? { ...row, customized_langs: langs, updated_at: new Date().toISOString() }
      : row)));
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 pt-1 lg:px-7">
      {notice && (
        <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 text-[13px] ${
          notice.tone === 'ok' ? 'bg-[#ECFDF5] text-[#047857]' : notice.tone === 'warn' ? 'bg-[#FFF8EB] text-[#92400E]' : 'bg-[#FDECEF] text-[#B91C1C]'
        }`}>
          <span>{notice.text}</span>
          {notice.action && <LinkButton onClick={notice.action.onClick}>{notice.action.label}</LinkButton>}
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
        <Card className="flex min-h-0 flex-col overflow-hidden px-3 pb-3 pt-5">
          <div className="flex items-center justify-between px-3">
            <SectionLabel>Modèles{items.length ? ` · ${items.length}` : ''}</SectionLabel>
            <LinkButton onClick={() => { setNotice(null); setComposer({ seed: null }); }}>Nouvelle campagne</LinkButton>
          </div>
          <div className="relative mx-3 mb-1 mt-3.5">
            <LineIcon d={ICONS.search} size={16} color="#9CA3AF" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Chercher un e-mail"
              className="h-10 w-full rounded-full border border-[#E5E7EB] bg-white pl-10 pr-4 text-[13.5px] placeholder-[#9CA3AF] focus:border-[#C7C4F6] focus:outline-none" />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-2 p-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded-2xl bg-[#EEF0F5]" />)}</div>
            ) : loadError ? (
              <div className="px-3 py-8 text-center text-[13px] text-[#B91C1C]">{loadError}<button type="button" onClick={load} className="mt-2 block w-full font-bold text-[#4F46E5]">Réessayer</button></div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-10 text-center text-[13px] text-[#6B7280]">Aucun e-mail ne correspond.</p>
            ) : filtered.map((group) => (
              <div key={group.id}>
                <p className="px-3 pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-[0.4px] text-[#9CA3AF]">{group.label}</p>
                {group.items.map((item) => {
                  const active = !composer && selected?.source === item.source && selected?.key === item.key;
                  return (
                    <button key={`${item.source}:${item.key}`} type="button"
                      onClick={() => { setComposer(null); setSelected({ source: item.source, key: item.key }); }}
                      className={`flex w-full items-center gap-3 rounded-2xl border-2 px-3.5 py-2.5 text-left transition-colors ${active ? 'border-[#C7C4F6] bg-[#F5F4FE]' : 'border-transparent hover:bg-[#F7F7FB]'}`}>
                      <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[#EFEEFD]"><LineIcon d={ICONS.mail} size={17} color="#4F46E5" /></span>
                      <span className="min-w-0 flex-1">
                        <b className="block truncate text-[14px] font-bold text-[#1E293B]">{item.label}</b>
                        <span className="block truncate font-mono text-[11.5px] text-[#9CA3AF]">{item.key}</span>
                      </span>
                      {item.customized_langs.length > 0 && <StatusPill tone="amber">Réécrit</StatusPill>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>

        <Card className="flex min-h-[60vh] flex-col overflow-hidden">
          {composer ? (
            <div className="overflow-y-auto px-7 py-6">
              <CampaignEmailComposer
                key={composer.seed ? `${composer.seed.source}:${composer.seed.key}` : 'blank'}
                token={token || ''}
                seed={composer.seed}
                onCancel={() => setComposer(null)}
                onCreated={() => {
                  setComposer(null);
                  setNotice({
                    tone: 'ok',
                    text: 'Campagne e-mail enregistrée, en attente de validation.',
                    action: { label: 'Relire et envoyer dans Campagnes', onClick: () => navigate('/communication') }
                  });
                }}
              />
            </div>
          ) : selected ? (
            <TemplateView
              key={`${selected.source}:${selected.key}`}
              token={token || ''}
              source={selected.source}
              templateKey={selected.key}
              duplicating={duplicating}
              onDuplicate={() => duplicate(selected.source, selected.key)}
              onSaved={refreshSummary}
              onNotice={setNotice}
            />
          ) : (
            <p className="m-auto px-6 text-center text-[13.5px] text-[#6B7280]">Choisissez un e-mail pour le lire.</p>
          )}
        </Card>
      </div>
    </main>
  );
};

// --- Un modèle : aperçu et réécriture -------------------------------------------------------

interface TemplateViewProps {
  token: string;
  source: TemplateSource;
  templateKey: string;
  duplicating: boolean;
  onDuplicate: () => void;
  onSaved: (detail: TemplateDetail) => void;
  onNotice: (notice: Notice) => void;
}

const TemplateView = ({ token, source, templateKey, duplicating, onDuplicate, onSaved, onNotice }: TemplateViewProps) => {
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lang, setLang] = useState<CampaignLang>('fr');
  const [tab, setTab] = useState<'preview' | 'edit'>('preview');
  const [drafts, setDrafts] = useState<Partial<Record<CampaignLang, TemplateFields>>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getEmailTemplate(token, source, templateKey)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Modèle indisponible.'); });
    return () => { cancelled = true; };
  }, [token, source, templateKey]);

  const current = detail?.langs[lang];
  const startingPoint = useCallback((l: CampaignLang): TemplateFields => {
    const entry = detail?.langs[l];
    const base = entry?.prefill || emptyFields();
    const o = entry?.override;
    return {
      subject: o?.subject ?? base.subject ?? '',
      title: o?.title ?? base.title ?? '',
      html: o?.html ?? base.html ?? '',
      text: o?.text ?? base.text ?? ''
    };
  }, [detail]);
  const draft = drafts[lang] || startingPoint(lang);
  const dirtyLangs = LANGS.map((l) => l.code).filter((l) => {
    const d = drafts[l];
    if (!d) return false;
    const s = startingPoint(l);
    return (['subject', 'title', 'html', 'text'] as (keyof TemplateFields)[]).some((f) => (d[f] || '') !== (s[f] || ''));
  });
  const dirty = dirtyLangs.includes(lang);

  const setField = (field: keyof TemplateFields, value: string) =>
    setDrafts((all) => ({ ...all, [lang]: { ...(all[lang] || startingPoint(lang)), [field]: value } }));
  const { track, insert } = useInsertAtCursor(setField);

  /**
   * Ce qu'on envoie : un champ identique au point de départ du code n'est pas
   * réécrit (null) — il continuera de suivre le code s'il change.
   */
  const payload = (l: CampaignLang): Partial<TemplateFields> => {
    const d = drafts[l] || startingPoint(l);
    const prefill = detail?.langs[l].prefill || emptyFields();
    const keep = (field: keyof TemplateFields) => {
      const value = (d[field] || '').trim() ? (d[field] as string) : null;
      return value !== null && value === prefill[field] ? null : value;
    };
    return { subject: keep('subject'), title: source === 'account' ? keep('title') : null, html: keep('html'), text: keep('text') };
  };

  const preview = useDebouncedPreview(
    () => previewEmailTemplate(token, source, templateKey, lang, tab === 'edit' ? payload(lang) : null),
    [token, source, templateKey, lang, tab, tab === 'edit' ? JSON.stringify(draft) : '', detail?.langs[lang].override?.updated_at],
    tab === 'edit' ? 700 : 0
  );

  const save = async () => {
    setSaving(true);
    onNotice(null);
    try {
      const updated = await saveEmailTemplate(token, source, templateKey, lang, payload(lang));
      setDetail(updated);
      setDrafts((all) => { const next = { ...all }; delete next[lang]; return next; });
      onSaved(updated);
      onNotice({ tone: 'ok', text: updated.langs[lang].override
        ? `Réécriture enregistrée (${lang.toUpperCase()}) : elle s’applique aux prochains envois.`
        : `Rien de changé par rapport au texte d’origine (${lang.toUpperCase()}) : c’est lui qui part.` });
    } catch (err: any) {
      onNotice({ tone: 'error', text: err.message || 'Enregistrement impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setConfirmReset(false);
    setSaving(true);
    try {
      const updated = await resetEmailTemplate(token, source, templateKey, lang);
      setDetail(updated);
      setDrafts((all) => { const next = { ...all }; delete next[lang]; return next; });
      onSaved(updated);
      onNotice({ tone: 'ok', text: `Texte d’origine rétabli (${lang.toUpperCase()}).` });
    } catch (err: any) {
      onNotice({ tone: 'error', text: err.message || 'Retour impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    onNotice(null);
    try {
      const { to } = await sendEmailTemplateTest(token, source, templateKey, lang, tab === 'edit' ? payload(lang) : null);
      onNotice({ tone: 'ok', text: `E-mail de test envoyé à ${to} (données d’exemple, objet préfixé « [Test] »).` });
    } catch (err: any) {
      onNotice({ tone: 'error', text: err.message || 'Envoi de test impossible.' });
    } finally {
      setTesting(false);
    }
  };

  if (error) return <div className="m-auto px-6 py-10 text-center text-[13px] text-[#B91C1C]">{error}</div>;
  if (!detail || !current) {
    return (
      <div className="space-y-3 p-7">
        <div className="h-6 w-80 max-w-full animate-pulse rounded bg-[#EEF0F5]" />
        <div className="h-[420px] animate-pulse rounded-2xl bg-[#F7F7FB]" />
      </div>
    );
  }

  const customized = (Object.keys(detail.langs) as CampaignLang[]).filter((l) => detail.langs[l].override);
  const dir = LANGS.find((l) => l.code === lang)?.dir || 'ltr';
  const chips = [...current.variables, ...current.defaults];
  const state = current.override
    ? `réécrit${current.override.updated_by_name ? ` par ${current.override.updated_by_name}` : ''} le ${formatDateTime(current.override.updated_at)}`
    : detail.editable ? 'version d’origine' : 'modèle de campagne';
  const variableChip = (v: string, clickable: boolean) => (
    <button key={v} type="button" disabled={!clickable} onMouseDown={(e) => e.preventDefault()} onClick={() => clickable && insert(v)}
      className={`rounded-lg px-2 py-[3px] font-mono text-[11px] font-semibold ${v.startsWith('default.') ? 'bg-[#EFEEFD] text-[#4F46E5]' : 'bg-[#F1F2F8] text-[#475569]'} ${clickable ? 'hover:brightness-95' : 'cursor-default'}`}>
      {`{{${v}}}`}
    </button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-start gap-3 border-b border-[#EEF0F4] px-7 pb-4 pt-5">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[20px] font-extrabold text-[#1E293B]">{detail.label}</h3>
          <p className="mt-0.5 truncate text-[13.5px] text-[#6B7280]">{[SOURCE_LABEL[detail.source], state].filter(Boolean).join(' · ')}</p>
        </div>
        <Segmented ariaLabel="Langue" value={lang} onChange={(l) => { setLang(l as CampaignLang); setConfirmReset(false); }}
          options={LANGS.map((l) => ({ id: l.code, label: `${l.label}${customized.includes(l.code) ? ' •' : ''}` }))} />
      </div>

      {tab === 'preview' || !detail.editable ? (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="min-w-0 flex-1 overflow-y-auto bg-[#F7F7FB] p-6">
            <div className="mx-auto max-w-[620px] overflow-hidden rounded-[18px] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
              <p className="border-b border-[#EEF0F4] px-5 py-3.5 text-[13px]" dir={dir}>
                <span className="text-[#6B7280]">Objet </span><b>{current.override?.subject || current.original.subject}</b>
              </p>
              <PreviewFrame html={preview.html} error={preview.error} />
            </div>
            <p className="mt-3 text-center text-[12px] text-[#9CA3AF]">Aperçu avec des données d’exemple.</p>
          </div>
          <aside className="border-t border-[#EEF0F4] px-6 py-5 lg:w-[280px] lg:shrink-0 lg:border-l lg:border-t-0">
            {chips.length > 0 && (
              <>
                <SectionLabel>Variables</SectionLabel>
                <div className="mt-3 flex flex-wrap gap-1.5">{chips.map((v) => variableChip(v, false))}</div>
              </>
            )}
            <div className={chips.length ? 'mt-6' : ''}><SectionLabel>Actions</SectionLabel></div>
            <div className="-mx-6 mt-1">
              {detail.editable && <ActionRow first icon={ICONS.doc} title="Réécrire" subtitle={`En ${lang === 'fr' ? 'français' : lang === 'en' ? 'anglais' : 'arabe'}`} onClick={() => setTab('edit')} inset={24} />}
              <ActionRow first={!detail.editable} icon={ICONS.mail} tone="grey" title={testing ? 'Envoi…' : 'Envoyer un test'} subtitle="À votre adresse" onClick={sendTest} disabled={testing} inset={24} />
              <ActionRow icon={ICONS.megaphone} tone="grey" title={duplicating ? 'Création…' : 'En faire une campagne'} subtitle="Relue avant l’envoi" onClick={onDuplicate} disabled={duplicating} inset={24} />
            </div>
          </aside>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto 2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="px-7 py-5">
            <button type="button" onClick={() => setTab('preview')} className="mb-2 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#4F46E5]">
              <LineIcon d={ICONS.chevron} size={14} color="#4F46E5" width={2.4} className="rotate-180" />
              Retour à l’aperçu
            </button>
            {current.computed.length > 0 && (
              <p className="mt-2 rounded-2xl bg-[#FFF8EB] px-4 py-3 text-[13px] leading-[1.5] text-[#92400E]">
                Le code compose une partie de cet e-mail selon la situation (dates, propriétaire ou locataire, listes…).
                Ces passages sont repris par <code className="font-mono">{'{{default.…}}'}</code>, écrivez autour pour les garder justes.
              </p>
            )}
            <div dir={dir}>
              <FieldLabel>Objet</FieldLabel>
              <input type="text" value={draft.subject || ''} onFocus={track('subject')} onChange={(e) => setField('subject', e.target.value)} className={FIELD} />
              {source === 'account' && (
                <>
                  <FieldLabel>Titre dans l’e-mail</FieldLabel>
                  <input type="text" value={draft.title || ''} onFocus={track('title')} onChange={(e) => setField('title', e.target.value)} className={FIELD} />
                </>
              )}
              <FieldLabel>Corps (HTML)</FieldLabel>
              <textarea value={draft.html || ''} rows={12} onFocus={track('html')} onChange={(e) => setField('html', e.target.value)} className={`${AREA} font-mono text-[12.5px]`} />
              <p className="mt-1.5 text-[12px] text-[#6B7280]" dir="ltr">
                {source === 'account' ? 'L’en-tête Dingo et le pied de page sont ajoutés autour.' : 'La salutation, le renvoi vers l’app, la signature et le pied de page sont ajoutés autour.'}
              </p>
              <FieldLabel>Texte brut, pour les messageries sans HTML</FieldLabel>
              <textarea value={draft.text || ''} rows={4} onFocus={track('text')} onChange={(e) => setField('text', e.target.value)} className={`${AREA} text-[13px]`} />
            </div>
            {chips.length > 0 && (
              <>
                <FieldLabel>Variables, un clic les insère au curseur</FieldLabel>
                <div className="flex flex-wrap gap-1.5">{chips.map((v) => variableChip(v, true))}</div>
              </>
            )}
            {confirmReset && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#F7F7FB] px-4 py-3">
                <p className="text-[13px] font-semibold text-[#1E293B]">Effacer la réécriture ({lang.toUpperCase()}) et revenir au texte du code ?</p>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setConfirmReset(false)} className="text-[13px] font-bold text-[#6B7280]">Annuler</button>
                  <button type="button" onClick={reset} className="text-[13px] font-bold text-[#B91C1C]">Revenir à l’origine</button>
                </div>
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              {current.override && !confirmReset && <LinkButton disabled={saving} onClick={() => setConfirmReset(true)}>Texte d’origine</LinkButton>}
              {dirty && (
                <button type="button" onClick={() => setDrafts((all) => { const next = { ...all }; delete next[lang]; return next; })} className="text-[13px] font-bold text-[#6B7280]">
                  Annuler les modifications
                </button>
              )}
              <BigButton kind="secondary" disabled={testing} onClick={sendTest}>{testing ? 'Envoi…' : 'M’envoyer ce brouillon'}</BigButton>
              <BigButton disabled={!dirty || saving} onClick={save}>{saving ? 'Enregistrement…' : `Enregistrer (${lang.toUpperCase()})`}</BigButton>
            </div>
          </div>
          <div className="bg-[#F7F7FB] px-6 py-5">
            <SectionLabel>Aperçu du brouillon</SectionLabel>
            <div className="mt-3 overflow-hidden rounded-[18px] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
              <PreviewFrame html={preview.html} error={preview.error} height="h-[640px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Campagne e-mail ------------------------------------------------------------------------

interface ComposerProps {
  token: string;
  seed: CampaignDraft | null;
  onCancel: () => void;
  onCreated: () => void;
}

type ComposerText = { subject: string; html: string; text: string };

const CampaignEmailComposer = ({ token, seed, onCancel, onCreated }: ComposerProps) => {
  const [lang, setLang] = useState<CampaignLang>('fr');
  const [texts, setTexts] = useState<Record<CampaignLang, ComposerText>>(() => {
    const from = (l: CampaignLang): ComposerText => {
      const c = seed?.content[l];
      return { subject: c?.subject || '', html: c?.html || '', text: c?.body || '' };
    };
    return { fr: from('fr'), ar: from('ar'), en: from('en') };
  });
  const [audience, setAudience] = useState<CampaignAudience>(seed?.audience && seed.audience in AUDIENCE_LABEL ? seed.audience as CampaignAudience : 'all');
  const [note, setNote] = useState(seed ? `Campagne e-mail tirée de « ${seed.label} »` : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const text = texts[lang];
  const dir = LANGS.find((l) => l.code === lang)?.dir || 'ltr';
  const setField = (field: keyof ComposerText, value: string) =>
    setTexts((all) => ({ ...all, [lang]: { ...all[lang], [field]: value } }));

  const content = useMemo((): Record<CampaignLang, CampaignText> => {
    const toCampaign = (t: ComposerText): CampaignText => {
      const body = (t.text.trim() || stripHtml(t.html)).slice(0, 240);
      return { title: t.subject.trim(), subject: t.subject.trim(), html: t.html.trim(), body };
    };
    return { fr: toCampaign(texts.fr), ar: toCampaign(texts.ar), en: toCampaign(texts.en) };
  }, [texts]);

  // Variables qu'une campagne ne sait pas remplir (elles s'effaceraient à
  // l'envoi), langue par langue : une langue vide reprend le français.
  const unresolved = useMemo(() => LANGS.map(({ code }) => {
    const found = new Set<string>();
    const t = texts[code];
    `${t.subject} ${t.html} ${t.text}`.replace(VARIABLE_RE, (match, name: string) => {
      if (!CAMPAIGN_VARIABLES.includes(name)) found.add(name);
      return match;
    });
    return { lang: code, names: Array.from(found).sort() };
  }).filter((entry) => entry.names.length > 0), [texts]);

  const frReady = content.fr.subject.length > 0 && content.fr.body.length > 0;
  const canSave = frReady && !saving;

  const preview = useDebouncedPreview(
    () => (frReady ? previewCampaignContent(token, content, lang) : Promise.resolve('')),
    [token, lang, JSON.stringify(content)]
  );

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await createCampaign(token, {
        kind: seed?.kind || 'custom',
        content,
        channels: ['email'],
        audience,
        link: seed?.link ?? null,
        note: note.trim() || null
      });
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-semibold text-gray-900">Nouvelle campagne e-mail</h3>
          <p className="text-[12.5px] text-gray-500">
            {seed ? <>À partir de « {seed.label} ». </> : null}
            Le français est obligatoire ; une langue laissée vide reprend le français.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900">Annuler</button>
          <button type="button" disabled={!canSave} onClick={submit} className="px-3.5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Enregistrement…' : 'Mettre en attente'}
          </button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700">{error}</div>}
      {seed?.uses_sample_data && (
        <div className="mt-4 rounded-xl bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900">
          Une partie du texte vient des données d’exemple de l’aperçu (Sami Ben Ali, Peugeot 208, dates…) : une campagne n’a pas de location derrière elle. Relisez et remplacez ces passages.
        </div>
      )}
      {unresolved.length > 0 && (
        <div className="mt-4 rounded-xl bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900">
          Une campagne ne sait remplir que <code className="font-mono">{'{{name}}'}</code> ; les autres variables seraient effacées à l’envoi. Remplacez-les par du texte :
          <ul className="mt-1 space-y-0.5">
            {unresolved.map((entry) => (
              <li key={entry.lang}>
                <button type="button" onClick={() => setLang(entry.lang)} className="font-medium underline">{LANGS.find((l) => l.code === entry.lang)?.label}</button>
                {' '}: {entry.names.map((v) => <code key={v} className="mx-0.5 font-mono">{`{{${v}}}`}</code>)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-5">
        <div>
          <LangTabs lang={lang} onChange={setLang} />
          <div className="mt-4 space-y-4" dir={dir}>
            <div>
              <label className={labelClass}>Objet</label>
              <input type="text" value={text.subject} maxLength={120} onChange={(e) => setField('subject', e.target.value)} placeholder="Ex : Ce week-end, sortez de Tunis" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Corps (HTML)</label>
              <textarea value={text.html} rows={12} onChange={(e) => setField('html', e.target.value)} placeholder="<p>Bonjour {{name}},</p><p>…</p>" className={`${inputClass} font-mono text-[12px]`} />
              <p className="mt-1 text-[11px] text-gray-400" dir="ltr">
                <code className="font-mono">{'{{name}}'}</code> = prénom du destinataire. Si le corps ne l’emploie pas, « Bonjour Prénom » est ajouté en tête ; la mention de désinscription est ajoutée en pied.
              </p>
            </div>
            <div>
              <label className={labelClass}>Texte brut <span className="font-normal text-gray-400">(sinon : tiré du HTML)</span></label>
              <textarea value={text.text} rows={4} onChange={(e) => setField('text', e.target.value)} className={`${inputClass} text-[12.5px]`} />
            </div>
          </div>

          <div className="mt-5">
            <p className={asideTitle}>Aperçu ({lang.toUpperCase()})</p>
            {frReady ? (
              <PreviewFrame html={preview.html} error={preview.error} />
            ) : (
              <p className="rounded-xl border border-dashed border-gray-200 px-3 py-10 text-center text-xs text-gray-500">
                L’aperçu s’affiche dès que l’objet et le corps en français sont remplis.
              </p>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-gray-100 p-3 space-y-3">
            <div>
              <p className={asideTitle}>Audience</p>
              <select value={audience} onChange={(e) => setAudience(e.target.value as CampaignAudience)} className={inputClass}>
                {Object.entries(AUDIENCE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <p className={asideTitle}>Canal</p>
              <p className="text-sm text-gray-700">E-mail seulement</p>
              <p className="mt-0.5 text-[11px] text-gray-400">Un push peut s’y ajouter dans l’onglet Campagnes, avant validation.</p>
            </div>
            <div>
              <p className={asideTitle}>Note interne</p>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Pourquoi cette campagne" className={inputClass} />
            </div>
          </div>
          <p className="text-[11px] text-gray-400">
            Rien ne part d’ici : la campagne se met en attente et s’envoie depuis l’onglet Campagnes, après relecture.
            Les comptes qui ont coupé les notifications promotionnelles ne la reçoivent pas ; chacun la reçoit dans sa langue.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default ManagementEmailTemplates;
